import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useForm, type FieldValues} from "react-hook-form";
import {Button, Spinner} from "@bolteu/clay-react";

import {useAppDispatch} from "webform/store/hooks";

import {DropdownController, FormSubmitted, Input} from "webform/components";
import {
    hasAnyAttachmentErrorSelector,
    isAnyAttachmentUploadingSelector,
    isWebformAvailableLoadingSelector,
    isWebformAvailableSelector,
    localeSelector,
    submitFormFieldsWithErrorSelector,
    submitFormIsSubmittedSelector,
    submitFormLoadingSelector,
    uploadedAttachmentIdsSelector,
    userInformationSelector,
} from "webform/store/selectors";
import {getUserPersonasList} from "webform/slices/userPersonaSlice";
import {SupportContactFormService} from "webform/gen/supportContactForm";
import {useGeoData} from "webform/hooks";
import {submitFormAction} from "webform/slices/submitFormSlice";
import {assertWebformAvailable} from "webform/slices/configSlice";
import {useI18n} from "i18n/i18n";
import {type FormValues} from "webform/utils";
import PhoneNumberValidator from "webform/utils/phoneValidator";
import {FORM_DESCRIPTION_MAX_LENGTH} from "webform/config/form";
import {DeeplinkAction, generateDeeplink, webformUserPersonaToMobileAppMap} from "webform/utils/deeplink";
import {mobileCheck} from "utils/mobileCheck";
import {brandSelector} from "store/selectors/brandSelector";

import * as Styled from "./form.styled";
import {Attachments, PageTitle, UserPersonaCombined} from "./components";

const isMobile = mobileCheck();

const formDefaultValues = {
    first_name: "",
    last_name: "",
    phone_number: "",
    description: "",
    user_persona_primary: null,
    user_persona_secondary: null,
    user_persona_description: "",
    selectedCountry: null,
    city_id: null,
};

export const FormWrapper = () => {
    const dispatch = useAppDispatch();

    const isFormSubmitted = useSelector(submitFormIsSubmittedSelector);
    const isWebformAvailableLoading = useSelector(isWebformAvailableLoadingSelector);
    const isWebformAvailable = useSelector(isWebformAvailableSelector);
    const userInformation = useSelector(userInformationSelector);

    useEffect(() => {
        dispatch(assertWebformAvailable());
    }, [dispatch]);

    const defaultValues = useMemo(
        () => ({
            ...formDefaultValues,
            first_name: userInformation?.first_name ?? "",
            last_name: userInformation?.last_name ?? "",
            phone_number: userInformation?.phone ?? "",
        }),
        [userInformation],
    );

    if (isWebformAvailableLoading) {
        return (
            <Styled.LoaderContainer>
                <Spinner />
            </Styled.LoaderContainer>
        );
    }

    if (isFormSubmitted || (!isWebformAvailable && !isWebformAvailableLoading)) {
        return <FormSubmitted />;
    }

    return <Form defaultValues={defaultValues} />;
};

interface OwnProps {
    defaultValues: FormValues;
}

const Form = ({defaultValues}: OwnProps) => {
    const dispatch = useAppDispatch();
    const i18n = useI18n();

    const isFormSubmitLoading = useSelector(submitFormLoadingSelector);
    const isAnyAttachmentUploading = useSelector(isAnyAttachmentUploadingSelector);
    const hasAnyAttachmentError = useSelector(hasAnyAttachmentErrorSelector);
    const uploadedAttachments = useSelector(uploadedAttachmentIdsSelector);
    const backendFailedFields = useSelector(submitFormFieldsWithErrorSelector);
    const selectedLocale = useSelector(localeSelector);
    const brand = useSelector(brandSelector);
    const [isFormManuallySet, setIsFormManuallySet] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(false);
    const {
        control,
        handleSubmit,
        watch,
        resetField,
        formState: {errors},
        setError,
    } = useForm<FieldValues>({defaultValues});

    const {city_id, selectedCountry, user_persona_primary: user_persona} = watch();

    const {citiesByCountry, countries} = useGeoData(selectedCountry?.title);

    const getDeeplink = useCallback(() => {
        const mobileAppFromPersona =
            user_persona && webformUserPersonaToMobileAppMap[user_persona.id as SupportContactFormService.UserPersona];
        if (mobileAppFromPersona) {
            return generateDeeplink(DeeplinkAction.OPEN_SUPPORT, mobileAppFromPersona, brand);
        }
        return undefined;
    }, [brand, user_persona]);

    const deeplink = getDeeplink();

    const showMobileDeeplink = useMemo(() => isMobile && user_persona && deeplink, [deeplink, user_persona]);

    const openDeeplink = () => {
        if (deeplink) {
            deeplink.navigate();
        }
    };

    const handleOpenFormManually = () => setIsFormManuallySet(true);

    const uploadedAttachmentsRef = useRef(uploadedAttachments);
    uploadedAttachmentsRef.current = uploadedAttachments;

    const doSubmit = useCallback(
        (data: FieldValues) => {
            const {first_name, last_name, phone_number, description} = data;
            if (data.city_id && data.user_persona_primary) {
                const persona =
                    data.user_persona_primary?.type === SupportContactFormService.UserPersonaDataType.SINGLE
                        ? data.user_persona_primary.persona
                        : data.user_persona_secondary?.persona;
                if (persona) {
                    dispatch(
                        submitFormAction({
                            city_id: parseInt(data.city_id.id, 10),
                            user_persona: persona,
                            user_persona_description: data.user_persona_description || undefined,
                            first_name,
                            last_name,
                            phone_number,
                            description,
                            attachment_ids:
                                uploadedAttachmentsRef.current.length > 0 ? uploadedAttachmentsRef.current : undefined,
                        }),
                    );
                }
            }
        },
        [dispatch],
    );

    const onSubmit = handleSubmit((data) => {
        if (isAnyAttachmentUploading) {
            setPendingSubmit(true);
            return;
        }
        doSubmit(data);
    });

    useEffect(() => {
        if (pendingSubmit && !isAnyAttachmentUploading) {
            setPendingSubmit(false);
            if (!hasAnyAttachmentError) {
                handleSubmit(doSubmit)();
            }
        }
    }, [pendingSubmit, isAnyAttachmentUploading, hasAnyAttachmentError, handleSubmit, doSubmit]);

    useEffect(() => {
        if (backendFailedFields.length) {
            backendFailedFields.forEach((field) => {
                setError(field, {type: "server", message: i18n("webform.form_field.error")});
            });
        }
    }, [backendFailedFields, i18n, setError]);

    useEffect(() => {
        if (selectedCountry) {
            resetField("city_id");
            resetField("user_persona_primary");
            resetField("user_persona_secondary");
            resetField("user_persona_description");
        }
    }, [resetField, selectedCountry]);

    useEffect(() => {
        if (city_id) {
            resetField("user_persona_primary");
            resetField("user_persona_secondary");
            resetField("user_persona_description");
            dispatch(getUserPersonasList({locale: selectedLocale, city_id: Number(city_id.id)}));
        }
    }, [city_id, dispatch, selectedLocale, resetField]);

    useEffect(() => {
        if (user_persona) {
            resetField("user_persona_secondary");
            resetField("user_persona_description");
        }
    }, [user_persona, resetField]);

    const validatePhoneNumber = (value: string) =>
        PhoneNumberValidator.isPossiblyValidPhoneNumber(value) || i18n("webform.form_field.error");

    return (
        <>
            <PageTitle />
            <DropdownController
                label={i18n("webform.fields.country")}
                required
                fieldName="selectedCountry"
                placeholder={i18n("webform.fields.select_country")}
                items={countries}
                control={control}
                noTopSpacing
            />
            {!!selectedCountry && (
                <DropdownController
                    label={i18n("webform.fields.city")}
                    required
                    fieldName="city_id"
                    placeholder={i18n("webform.fields.select_city")}
                    groupedItems={citiesByCountry}
                    control={control}
                />
            )}
            {!!city_id && (
                <>
                    <UserPersonaCombined control={control} user_persona={user_persona} />
                    {user_persona && (
                        <>
                            {showMobileDeeplink && !isFormManuallySet ? (
                                <>
                                    <Styled.Spacer />
                                    <Button className="w-full" variant="primary" onClick={openDeeplink}>
                                        {i18n("webform.mobile.open_bolt.app", {brand})}
                                    </Button>
                                    <Button className="w-full" variant="tertiary" onClick={handleOpenFormManually}>
                                        {i18n("webform.mobile.no_access_to_app", {brand})}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Input
                                        label={i18n("webform.fields.first_name")}
                                        required
                                        fieldName="first_name"
                                        control={control}
                                    />
                                    <Input
                                        label={i18n("webform.fields.last_name")}
                                        required
                                        fieldName="last_name"
                                        control={control}
                                    />
                                    <form onSubmit={onSubmit} noValidate>
                                        <Input
                                            label={i18n("webform.fields.phone")}
                                            required
                                            type="tel"
                                            fieldName="phone_number"
                                            placeholder="+372 0000 0000"
                                            validationFunction={validatePhoneNumber}
                                            control={control}
                                        />
                                        <Input
                                            label={i18n("webform.fields.additional_comments")}
                                            subtitle={`${watch("description")?.length ?? 0}/${FORM_DESCRIPTION_MAX_LENGTH}`}
                                            fieldName="description"
                                            multiline
                                            maxLength={FORM_DESCRIPTION_MAX_LENGTH}
                                            placeholder={i18n("webform.fields.describe_your_issue_placeholder")}
                                            defaultValue=""
                                            control={control}
                                        />
                                        <Attachments />
                                        <Styled.Spacer />
                                        <Button
                                            className="w-full"
                                            disabled={Object.keys(errors).length !== 0}
                                            type="submit"
                                            loading={isFormSubmitLoading || pendingSubmit}
                                            variant="primary"
                                        >
                                            {i18n("webform.button.submit")}
                                        </Button>
                                    </form>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
};
